import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react'
import { Button, Modal } from "react-bootstrap"
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { IMessageMatchTemplateParsedField, IMessageMatchTemplateResp, IMessageSenderLookup, IMessageTemplate } from '../../core/response.types'
import { MessageOperation } from '../../core/types'
import { MASK_OPTIONS } from '../../core/consts'
import { useNavigate } from 'react-router-dom'
import { generateTemplate, messageMatchTemplate, saveTemplate } from '../../core/api'
import Select from 'react-select'
import Swal from 'sweetalert2'
import { SelectStyles } from "../../../../../../../components/Select"
import { useThemeMode } from "../../../../../../../_metronic/partials"

interface CreateTemplateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave?: (template: IMessageTemplate) => void
  lookupSenders: IMessageSenderLookup[]
}

export default ({
  open,
  onOpenChange,
  onSave,
  lookupSenders,
}: CreateTemplateModalProps) => {
  const { mode: theme } = useThemeMode()
  const navigate = useNavigate()
  // ===== СОСТОЯНИЯ ФЛОУ =====
  const [step, setStep] = useState<
    | 'sender_input'
    | 'sender_search'
    | 'sender_not_found'
    | 'form'
    | 'template_search'
    | 'template_found'
    | 'template_not_found'
    | 'generating'
    | 'mask_editor'
    | 'testing'
  >('sender_input')

  // ===== ДАННЫЕ ФОРМЫ =====
  const [sender, setSender] = useState<IMessageSenderLookup | null>(null)
  const [messageType, setMessageType] = useState<MessageOperation>('DEPOSIT')
  const [messageText, setMessageText] = useState('')

  // ===== РЕЗУЛЬТАТЫ =====
  const [foundTemplate, setFoundTemplate] = useState<IMessageMatchTemplateResp | null>(null)
  const [generatedMask, setGeneratedMask] = useState('')
  const [parsedFields, setParsedFields] = useState<IMessageMatchTemplateParsedField[]>([])
  const [generatedCacheId, setGeneratedCacheId] = useState('')

  // ===== UI СОСТОЯНИЯ =====
  const [isFormLocked, setIsFormLocked] = useState(false)
  const [showSaveButton, setShowSaveButton] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // ===== АВТОДОПОЛНЕНИЕ ОТПРАВИТЕЛЯ =====
  const [showSenderDropdown, setShowSenderDropdown] = useState(false)

  // ===== РЕДАКТОР МАСКИ =====
  const MENU_GAP = 10;      // тот самый отступ 10px
  const VIEWPORT_PAD = 8;   // небольшой внутренний паддинг от краёв экрана
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isManualEdit, setIsManualEdit] = useState(false)
  const [selectionMenu, setSelectionMenu] = useState<{
    visible: boolean
    x: number
    y: number
    anchorX: number,
    anchorY: number,
    selectedText: string
    start: number
    end: number
  }>({ visible: false, x: 0, y: 0, anchorX: 0, anchorY:0, selectedText: '', start: 0, end: 0 })
  const maskTextareaRef = useRef<HTMLTextAreaElement>(null)

  // ===== ОБРАБОТЧИКИ =====

  // Выбор отправителя из списка
  const handleSelectSender = useCallback((selectedSender: any) => {  
    if(selectedSender === null) {
        setSender(null)
        return
    }
    var reqTypeFind = lookupSenders.find(v => v.id === selectedSender!.value)
    if(reqTypeFind !== undefined) {
      setSender(reqTypeFind)
      setStep('form')
    }
  }, [])

  // Добавление нового отправителя
  const handleAddSender = useCallback((value: string) => {
    setShowSenderDropdown(false)
    navigate('/settings/message/senders#newSender', {
        state: {
          initialSender: value,
        },
    })
    
  }, [])

  // Попытка сметчить сообщение и найти шаблон
  const handleSearchTemplate = useCallback(async () => {
    setStep('template_search')
    setIsFormLocked(true)
    try {
      const response = await messageMatchTemplate({
        requisiteType: sender?.requisiteTypeCode!,
        operation: messageType,
        message: messageText,
        channel: sender?.channel!
      })
      if (!response?.data?.success) {
        throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
        )
      }
      
      if (response.data.data) {
        setFoundTemplate(response.data.data)
        setStep('template_found')
      } else {
        setStep('template_not_found')
      }
    } catch (error: any) {
        Swal.fire('Ошибка', 'Произошла ошибка метча шаблона: ' + error.data.errors.general_error, 'error')
        console.error('Ошибка метча шаблона:', error)
        setIsFormLocked(false)
        setStep('form')
    } 
  }, [sender, messageType, messageText])

  const handleGenerateTemplate = useCallback(async () => {
    setStep('generating')
    try {
      const response = await generateTemplate({
        requisiteType: sender?.requisiteTypeCode!,
        operation: messageType,
        message: messageText,
        channel: sender?.channel!
      })

      if (!response?.data?.success) {
        throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
        )
      }

      if (response.data.data) {
        setGeneratedMask(response.data.data.mask)
        setParsedFields(response.data.data.parsedFields)
        setGeneratedCacheId(response.data.data.cacheId)
        setStep('mask_editor')
        setShowSaveButton(true)
      } else {
        setStep('template_not_found')
      }
    } catch (error: any) {
        Swal.fire('Ошибка', 'Произошла ошибка генерации шаблона: ' + (error?.data?.errors?.general_error ?? 'Unknown error'), 'error')
        console.error('Ошибка генерации шаблона:', error)
        setStep('template_not_found')
    } 
  }, [messageText])

  const handleResetMask = useCallback(() => {
    // Сбрасываем маску к исходному тексту сообщения
    setGeneratedMask(messageText)
    setIsManualEdit(true)
    setParsedFields([])
    setShowSaveButton(false)
  }, [messageText])

  const handleTestMask = useCallback(async () => {
    setStep('testing')
    setShowSaveButton(false)
    try {
      const response = await generateTemplate({
        requisiteType: sender?.requisiteTypeCode!,
        operation: messageType,
        message: messageText,
        channel: sender?.channel!,
        mask: generatedMask,
      })

      if (!response?.data?.success) {
        throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
        )
      }

      if (response.data.data) {
        setGeneratedMask(response.data.data.mask)
        setParsedFields(response.data.data.parsedFields)
        setGeneratedCacheId(response.data.data.cacheId)
        setStep('mask_editor')
        setShowSaveButton(true)
        setIsManualEdit(false)
      } else {
        setStep('template_not_found')
      }
    } catch (error: any) {
        Swal.fire('Ошибка', 'Произошла ошибка тестирования маски: ' + (error?.data?.errors?.general_error ?? 'Unknown error'), 'error')
        console.error('Ошибка тестирования маски:', error)
        setStep('template_not_found')
    } 
  }, [generatedMask])

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    setShowSaveButton(false)
    try {
      const response = await saveTemplate({ cacheId: generatedCacheId })

      if (!response?.data?.success) {
        throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
        )
      }

      if (response.data.data) {        
        onSave?.(response.data.data)
        onOpenChange(false)
      } else {
        throw new Error('Response data is empty')
      }
    } catch (error: any) {
      Swal.fire('Ошибка', 'Произошла ошибка сохранения шаблона: ' + (error?.data?.errors?.general_error ?? 'Unknown error'), 'error')
      console.error('Ошибка сохранения шаблона:', error)
    }
    setIsSaving(false)
    setStep('mask_editor')
  }, [generatedMask, parsedFields, messageType, sender, onSave, onOpenChange])

  const handleUnlockForm = useCallback(() => {
    setIsFormLocked(false)
    setStep('form')
    setFoundTemplate(null)
  }, [])

  // ===== ОБРАБОТКА ВЫДЕЛЕНИЯ ТЕКСТА =====

  const lastPointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMouseUp = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    handleTextSelection(); // можно вызывать тут, чтобы меню появлялось сразу после выделения
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLTextAreaElement>) => {
    const t = e.changedTouches[0];
    if (t) lastPointerRef.current = { x: t.clientX, y: t.clientY };
    handleTextSelection();
  };

  const handleTextSelection = useCallback(() => {
    if (!maskTextareaRef.current) return
    
    // Разрешаем выделение только в режиме ручного редактирования
    if (!isManualEdit) return
    
    const textarea = maskTextareaRef.current
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    
    // Если ничего не выделено - скрываем меню
    if (start === end) {
      setSelectionMenu((prev) => ({ ...prev, visible: false }))
      return
    }
    
    const selectedText = generatedMask.substring(start, end)
    
    // Не показываем меню если выделен только пробел или пустая строка
    if (!selectedText.trim()) {
      setSelectionMenu((prev) => ({ ...prev, visible: false }))
      return
    }

    const { x, y } = lastPointerRef.current;
    
    setSelectionMenu({
      visible: true,
      x: x,
      y: y,
      anchorX: x,
      anchorY: y,
      selectedText,
      start,
      end,
    })
  }, [isManualEdit, generatedMask])

  const handleMaskSelect = useCallback(
    (maskValue: string) => {
      const { start, end, selectedText } = selectionMenu

      // Добавляем в распаршенные поля
      const newField: IMessageMatchTemplateParsedField = {
        key: maskValue.replace(/[{}]/g, ''),
        value: selectedText,        
      }
      setParsedFields((prev) => [...prev, newField])

      // Заменяем текст в маске
      const newMask =
        generatedMask.substring(0, start) + maskValue + generatedMask.substring(end)
      setGeneratedMask(newMask)
      setSelectionMenu((prev) => ({ ...prev, visible: false }))
    },
    [selectionMenu, generatedMask]
  )

  // ===== ЗАКРЫТИЕ МЕНЮ ВЫДЕЛЕНИЯ =====

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Не закрываем если кликнули внутри меню или textarea
      const target = e.target as HTMLElement
      if (target.closest('[data-selection-menu]') || target.closest('textarea')) {
        return
      }
      setSelectionMenu((prev) => ({ ...prev, visible: false }))
    }
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectionMenu((prev) => ({ ...prev, visible: false }))
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const resetSenderFrom = () => {
    setStep('sender_input')
    setSender(null)
    setIsFormLocked(false)
  }

  const resetAllForm = () => {   
    resetSenderFrom()
    setMessageType('DEPOSIT')
    setMessageText('')
    setFoundTemplate(null)
    setGeneratedMask('')
    setParsedFields([])    
    setShowSaveButton(false)
    setIsManualEdit(false)
  }


  // ===== СБРОС ПРИ ЗАКРЫТИИ =====

  useEffect(() => {
    if (!open) {
      resetAllForm()
    }
  }, [open, resetAllForm])

  useLayoutEffect(() => {
    if (!selectionMenu.visible) return;
    const el = menuRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();

    const { x, y } = computeMenuPos({
      ax: selectionMenu.anchorX,
      ay: selectionMenu.anchorY,
      menuW: r.width,
      menuH: r.height,
      gap: MENU_GAP,
      pad: VIEWPORT_PAD,
    });

    // обновляем только если реально изменилось, чтобы не зациклить ререндеры
    setSelectionMenu((prev) => {
      if (!prev.visible) return prev;
      if (Math.abs(prev.x - x) < 0.5 && Math.abs(prev.y - y) < 0.5) return prev;
      return { ...prev, x, y };
    });
  }, [selectionMenu.visible, selectionMenu.anchorX, selectionMenu.anchorY]);

  useEffect(() => {
    if (!selectionMenu.visible) return;

    const hide = () => setSelectionMenu((p) => ({ ...p, visible: false }));

    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);

    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [selectionMenu.visible]);

  // ===== ПРОВЕРКА ЗАПОЛНЕННОСТИ ФОРМЫ =====

  const isFormComplete = sender && messageType && messageText.trim().length > 10

  // ===== РЕНДЕР =====
  return (
    <Modal 
      show={open} 
      onHide={() => {!isSaving && onOpenChange(false)}} 
      centered
      onEscapeKeyDown={(e) => {
          if (isSaving) {
              e.preventDefault()
              return
          }
          if (showSenderDropdown) {
              e.preventDefault()
              setShowSenderDropdown(false)
              return
          }
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Создание шаблона</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fv-row mb-8">
          {/* ===== БЛОК: ПОИСК ОТПРАВИТЕЛЯ ===== */}
          <div className={isFormLocked ? 'opacity-50 pe-none' : ''}>
            <label className="required fs-6 fw-semibold mb-2">Укажите отправителя</label>      
            <Select     
              styles={SelectStyles(theme)}     
              className='mb-8'              
              onChange={handleSelectSender}
              isClearable
              value={sender ? { value: sender.id, label: sender.channel + '|' + sender.requisiteTypeName + '|' + sender.name } : undefined}
              options={lookupSenders.map(v => ({value: v.id, label: v.channel + '|' + v.requisiteTypeName + '|' + v.name}))}
              placeholder={'Начните вводить название...'}
              formatOptionLabel={(option, meta) => {
                const [channel, requisiteTypeName, name] = option.label.split('|');
                return meta.context === "menu" ? (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span className="d-flex flex-column">
                          <span className="fw-bold">{name}</span>
                          <span className="text-muted fs-8">{channel} | {requisiteTypeName}</span>
                      </span>
                  </div>
              ) : name}}
              noOptionsMessage={({ inputValue }) => (
                  <>
                      <div className="px-3">
                          <div className="px-3 py-2 text-gray-500">Отправитель не найден</div>
                      </div>
                      <div className="separator my-2" />
                      <div className="menu-item px-3">
                          <span
                              className="menu-link px-3 text-primary d-flex align-items-center gap-2"
                              onClick={() => handleAddSender(inputValue)}
                              style={{ cursor: 'pointer' }}
                              role="button"
                          >
                          <KTIcon iconName="plus" className="fs-4 text-primary" />
                          <span>
                              Создать отправителя &ldquo;{inputValue}&rdquo;
                          </span>
                          </span>
                      </div>
                  </>
              )}
            />    
          </div>

          {/* ===== БЛОК: РАБОТА с СООБЩЕНИЕМ ===== */}
          {(step === 'form' ||
            step === 'template_search' ||
            step === 'template_found' ||
            step === 'template_not_found' ||
            step === 'generating' ||
            step === 'mask_editor' ||
            step === 'testing') && sender && (
            <div className={isFormLocked ? 'opacity-50 pe-none' : ''}>
              <div className="row g-5 mb-8">
                <div className="col-md-4">
                  <label className="required fs-6 fw-semibold mb-2">Тип реквизита</label>
                  <input
                      type="text"
                      className="form-control form-control-solid"
                      value={sender?.requisiteTypeName || ''}
                      disabled
                  />
                </div>

                <div className="col-md-4">
                  <label className="required fs-6 fw-semibold mb-2">Канал доставки</label>
                  <input
                      type="text"
                      className="form-control form-control-solid"
                      value={sender?.channel || ''}
                      disabled
                  />
                </div>

                <div className="col-md-4">
                  <label className="required fs-6 fw-semibold mb-2">Тип сообщения</label>
                  <Select   
                    styles={SelectStyles(theme)}                   
                    onChange={(e) => setMessageType(e?.value as MessageOperation)}
                    isDisabled={isFormLocked}
                    value={{ value: messageType, label: messageType }}
                    options={[{value: 'DEPOSIT', label: 'DEPOSIT'},{value: 'PAY', label: 'PAY'},{value: 'SECURITY', label: 'SECURITY'},{value: 'OTHER', label: 'OTHER'}]}
                  />
                </div>
              </div>

              <div className="fv-row mb-8">
                <label className="required fs-6 fw-semibold mb-2">Текст сообщения</label>
                <textarea
                  className="form-control form-control-solid"
                  rows={4}
                  placeholder="Вставьте текст сообщения..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  disabled={isFormLocked}
                />
                <div className="form-text text-muted">
                  Минимум 10 символов.
                </div>
              </div>

              {/* Кнопка поиска шаблона */}
              {step === 'form' && isFormComplete && (
                <button type="button" className="btn btn-primary w-100" onClick={handleSearchTemplate}>
                  <KTIcon iconName="magnifier" className="fs-4 me-2" />
                  Проверить сообщение
                </button>
              )}

              {/* Индикатор поиска */}
              {step === 'template_search' && (
                <div className="d-flex align-items-center justify-content-center gap-3 py-6">
                  <span className="spinner-border spinner-border-sm align-middle" />
                  <span className="text-gray-600">Поиск шаблона . . . </span>
                </div>
              )}
            </div>
          )}

          {/* ===== БЛОК: ШАБЛОН НАЙДЕН ===== */}
          {step === 'template_found' && foundTemplate && (
            <div className="notice d-flex rounded border-success border border-dashed p-6 mb-8">
              {!!foundTemplate.enabledMatcher && !!foundTemplate.enabledTemplate ?
                <KTIcon iconName="check-circle" className="fs-2 text-success me-4" /> 
              :
                <KTIcon iconName="information-5" className="fs-2 text-warning me-4" />
              }
              <div className="fw-semibold flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fs-6 text-gray-700 fw-bold">Для указанного сообщения уже есть шаблон
                  {(!foundTemplate.enabledMatcher || !foundTemplate.enabledTemplate) && ', но сообщение с устройства не будет обработанно!'}
                  <div className="fw-normal">{!foundTemplate.enabledMatcher && (<>Метчер отключен<span className="badge badge-secondary ms-2">enabled=false</span></>)}</div>
                  <div className="fw-normal">{!foundTemplate.enabledTemplate && (<>Шаблон отключен<span className="badge badge-secondary ms-2">enabled=false</span></>)}</div>
                </div>

                <div className="text-gray-600 mt-2">
                  <div className="mb-1">
                    <span className="fw-bold">Matcher ID:</span>
                    {foundTemplate.id}
                  </div>

                  {foundTemplate.mask && (
                    <div className="mb-2">
                      <span className="fw-bold">Маска:</span>
                      <span 
                        className="badge badge-light-success ms-2"
                        style={{
                          display: "inline",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          boxDecorationBreak: "clone",
                          WebkitBoxDecorationBreak: "clone",
                          lineHeight: 2.3
                        }}
                      >
                        {foundTemplate.mask}
                      </span>
                    </div>
                  )}

                  <div className="mb-2">
                    <span className="fw-bold">Полученные поля:</span>
                    <div className="d-flex flex-wrap gap-2 ms-2">
                      {foundTemplate.parsedFields.map((f, i) => (
                        <span key={i} className="badge badge-light-success">
                          {f.key}: {f.value}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button type="button" className="btn btn-light btn-sm mt-4" onClick={handleUnlockForm}>
                    Разблокировать форму
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== БЛОК: ШАБЛОН НЕ НАЙДЕН ===== */}
          {step === 'template_not_found' && (
            <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-8">
              <KTIcon iconName="information-5" className="fs-2 text-warning me-4" />
              <div className="fw-semibold">
                <div className="fs-6 text-gray-900 fw-bold">Шаблон не найден</div>
                <div className="text-gray-700 mt-2">
                  Для указанного сообщений шаблон не найден. Хотите сгенерировать новый?
                  <div className="d-flex flex-wrap gap-2 mt-4">
                    <button type="button" className="btn btn-primary" onClick={handleGenerateTemplate}>
                      <KTIcon iconName="abstract-25" className="fs-4 me-2" />
                      Сгенерировать шаблон
                    </button>

                    <button type="button" className="btn btn-light" onClick={handleUnlockForm}>
                      Изменить данные
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== БЛОК: ГЕНЕРАЦИЯ ===== */}
          {step === 'generating' && (
            <div className="d-flex flex-column align-items-center justify-content-center py-10">
              <span className="spinner-border align-middle mb-4" />
              <div className="text-gray-600">Генерация шаблона...</div>
            </div>
          )}

          {/* ===== БЛОК: РЕДАКТОР МАСКИ ===== */}
          {(step === 'mask_editor' || step === 'testing') && (
            <div className="mt-8">
              <div className="d-flex flex-stack flex-wrap gap-2 mb-4">
                <label className="fs-6 fw-semibold">Маска шаблона</label>

                {step === 'mask_editor' && generatedMask && (
                  <button type="button" className="btn btn-light btn-sm" onClick={handleResetMask}>
                    <KTIcon iconName="arrows-loop" className="fs-4 me-2" />
                    Сбросить маску
                  </button>
                )}
              </div>

              {/* Подсказка режима ручного редактирования */}
              {isManualEdit && step === 'mask_editor' && (
                <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-6">
                  <KTIcon iconName="information-5" className="fs-2 text-warning me-4" />
                  <div className="text-gray-800">
                    Режим ручного редактирования. Выделите текст мышью и выберите тип маски из меню.
                  </div>
                </div>
              )}

              <div className="position-relative">
                <textarea
                  ref={maskTextareaRef}                  
                  className={
                    'form-control form-control-solid font-monospace fs-7 ' +
                    (isManualEdit && step !== 'testing' ? 'bg-light-warning border-warning' : '')
                  }
                  rows={5}
                  value={generatedMask}
                  placeholder="Маска шаблона..."
                  disabled={step === 'testing'}
                  onChange={(e) => {
                    setGeneratedMask(e.target.value)
                    if (!isManualEdit) {
                      setIsManualEdit(true)
                      setParsedFields([])
                      setShowSaveButton(false)
                    }
                  }}
                  onMouseUp={handleMouseUp}
                  onTouchEnd={handleTouchEnd}
                  onDoubleClick={handleTextSelection}
                  onSelect={handleTextSelection}
                  onKeyUp={handleTextSelection}
                />

                {/* Overlay при testing */}
                {step === 'testing' && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-50 rounded"
                    style={{backdropFilter: 'blur(1px)'}}
                  >
                    <div className="d-flex flex-column align-items-center gap-2">
                      <span className="spinner-border spinner-border-sm" />
                      <span className="text-gray-600">Тестирование маски...</span>
                    </div>
                  </div>
                )}

                {/* Меню выбора маски при выделении */}
                {selectionMenu.visible && (
                  <div
                    ref={menuRef}
                    data-selection-menu
                    className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-4 show"
                    style={{
                      position: 'fixed',
                      left: selectionMenu.x,
                      top: selectionMenu.y,
                      zIndex: 110,
                      width: 320,
                      boxShadow: '0 0 50px 0 rgba(82, 63, 105, 0.15)',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 pb-3">
                      <div className="text-muted fs-8 mb-2">
                        Выбрано:{' '}
                        <span className="badge badge-light">{selectionMenu.selectedText}</span>
                      </div>
                      <div className="text-muted fs-8">Выберите тип маски:</div>
                    </div>

                    <div className="separator my-2" />

                    <div className="px-3">
                      <div className="row g-2 px-2">
                        {MASK_OPTIONS.map((o) => (
                          <div className="col-6" key={o.value}>
                            <button
                              type="button"
                              className="btn btn-light btn-sm w-100 text-start"
                              onClick={() => handleMaskSelect(o.value)}
                            >
                              <div className="fw-bold">{o.label}</div>
                              <div className="text-muted fs-8">{o.value}</div>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Распаршенные поля */}
              {parsedFields.length > 0 && (
                <div className="mt-6">
                  <div className="fs-6 fw-semibold mb-2">Распознанные поля</div>
                  <div className="d-flex flex-wrap gap-2">
                    {parsedFields.map((f, i) => (
                      <span key={i} className="badge badge-light">
                        <span className="fw-bold">{f.key}:</span> {f.value}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Кнопка тестирования (только ручной режим) */}
              {isManualEdit && step === 'mask_editor' && (
                <button type="button" className="btn btn-light w-100 mt-6" onClick={handleTestMask}>
                  <KTIcon iconName="flask" className="fs-4 me-2" />
                  Тестировать маску
                </button>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={() => onOpenChange(false)} disabled={isSaving}>Отменить</Button>
        <Button 
            variant='primary' 
            onClick={handleSave}
            disabled={isSaving || !isFormComplete || !generatedCacheId || !(parsedFields.length > 0)}
            style={{cursor: isSaving || !isFormComplete || !generatedCacheId || !(parsedFields.length > 0) ? 'not-allowed' : 'pointer'}}
        >
            {isSaving ? (
                <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Сохранение...
                </>
            ) : (
                'Сохранить'
            )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function computeMenuPos(params: {
  ax: number;
  ay: number;
  menuW: number;
  menuH: number;
  gap: number;
  pad: number;
}) {
  const { ax, ay, menuW, menuH, gap, pad } = params;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Горизонталь: по умолчанию справа от курсора
  let x = ax + gap;

  // Если справа не влезает — рисуем слева
  if (x + menuW > vw - pad) {
    x = ax - gap - menuW;
  }

  // Если и слева не влезает — центруем по курсору и клампим
  if (x < pad || x + menuW > vw - pad) {
    x = clamp(ax - menuW / 2, pad, vw - pad - menuW);
  }

  // Вертикаль: по умолчанию снизу от курсора
  let y = ay + gap;

  // Если снизу не влезает — рисуем сверху
  if (y + menuH > vh - pad) {
    y = ay - gap - menuH;
  }

  // Если и сверху не влезает — центруем по курсору и клампим
  if (y < pad || y + menuH > vh - pad) {
    y = clamp(ay - menuH / 2, pad, vh - pad - menuH);
  }

  return { x, y };
}
